import React from "react";
import PropTypes from "prop-types";
import { Box, ButtonBase, Typography, Select, MenuItem, useMediaQuery } from "@mui/material";
import usePagination from "@mui/material/usePagination";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useTranslation } from "react-i18next";

// Options derive from one base (base × [1..5]) so the choices don't shift as the user picks a larger size.
export const DEFAULT_PAGE_SIZE = 6;
const PAGE_SIZE_MULTIPLIERS = [1, 2, 3, 4, 5];
export const getPageSizeOptions = (base = DEFAULT_PAGE_SIZE) =>
  PAGE_SIZE_MULTIPLIERS.map((multiplier) => multiplier * base);

// Shared 32px-square cell geometry so pills, arrows, and ellipsis line up. Colour,
// radius, border and weight are layered on per item type.
const cellSx = (theme) => ({
  minWidth: theme.spacing(4), // 32px — WCAG 2.2 AA target
  height: theme.spacing(4),
  px: 1, // 8px — lets multi-digit page numbers breathe past the min width
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: theme.spacing(1), // 8px (Figma radius-md)
  fontFamily: "inherit",
  "&:focus-visible": {
    outline: `3px solid ${theme.palette.accent?.butterscotch}`,
    outlineOffset: "2px",
  },
});

const focusRing = (theme) => ({
  outline: `3px solid ${theme.palette.accent?.butterscotch}`,
  outlineOffset: "2px",
});

function PageControl({ items, t }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.5, sm: 1 }, maxWidth: "100%" }}>
      {items.filter(Boolean).map(({ type, page: pageNumber, selected, disabled, ...item }, index) => {
        if (type === "start-ellipsis" || type === "end-ellipsis") {
          return (
            <Box
              key={`${type}-${index}`}
              aria-hidden="true"
              sx={(theme) => ({ ...cellSx(theme), color: "text.textGrey" })}
            >
              …
            </Box>
          );
        }

        if (type === "previous" || type === "next") {
          const isPrev = type === "previous";
          return (
            <ButtonBase
              key={type}
              {...item}
              disabled={disabled}
              aria-label={t(isPrev ? "pagination.previous" : "pagination.next")}
              sx={(theme) => ({
                ...cellSx(theme),
                borderRadius: theme.spacing(0.5), // 4px (Figma radius-xs)
                color: "text.primary",
                "&.Mui-disabled": { opacity: 0.5 }, // Figma disabled arrow = 50%
              })}
            >
              {isPrev ? (
                <ChevronLeftIcon sx={{ fontSize: "1rem" }} />
              ) : (
                <ChevronRightIcon sx={{ fontSize: "1rem" }} />
              )}
            </ButtonBase>
          );
        }

        // Numbered page pill. Active = white bg + primary border + dark medium
        // text; inactive = plain hint-grey. Transparent border on inactive keeps
        // heights flush (no shift when the border appears on selection).
        return (
          <ButtonBase
            key={pageNumber}
            {...item}
            aria-label={t("pagination.goToPage", { page: pageNumber })}
            aria-current={selected ? "page" : undefined}
            sx={(theme) => ({
              ...cellSx(theme),
              border: `1px solid ${selected ? theme.palette.primary.interactive : "transparent"}`,
              backgroundColor: selected ? "background.paper" : "transparent",
              color: selected ? "text.primary" : "text.textGrey",
              fontSize: 14,
              lineHeight: "20px",
              fontWeight: selected ? 600 : 400, // 500/Medium isn't loaded → 600
              letterSpacing: selected ? "0.1px" : "0.25px",
            })}
          >
            {pageNumber}
          </ButtonBase>
        );
      })}
    </Box>
  );
}

PageControl.propTypes = {
  items: PropTypes.array.isRequired,
  t: PropTypes.func.isRequired,
};

// Figma "items control": Showing [size ▾] of {total} items. The <select> is the
// runtime page-size chooser; the noun stays generic ("items") per the Figma.
function ItemsControl({ pageSize, pageSizeOptions, onPageSizeChange, totalCount, t, language }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: "auto" }}>
      <Typography variant="caption" sx={{ color: "text.textGrey", whiteSpace: "nowrap" }}>
        {language === 'hi_IN' ? t("pagination.ofItems", { count: totalCount })  : t("pagination.showing")}
      </Typography>
      <Select
        size="small"
        value={pageSize}
        onChange={(event) => onPageSizeChange(Number(event.target.value))}
        IconComponent={KeyboardArrowDownIcon}
        inputProps={{ "aria-label": t("pagination.itemsPerPage") }}
        sx={(theme) => ({
          color: "text.textGrey",
          fontSize: 12,
          lineHeight: "16px",
          borderRadius: theme.spacing(1), // 8px (Figma radius-md)
          "& .MuiSelect-select": {
            py: 0.75, // ~6px
            pl: 1, // 8px
            pr: 3,
            minHeight: "unset",
          },
          "& .MuiOutlinedInput-notchedOutline": { borderColor: theme.palette.divider },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: theme.palette.divider },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.divider,
            borderWidth: "1px",
          },
          "&.Mui-focused": focusRing(theme),
          "& .MuiSelect-icon": { color: "text.textGrey", right: 4 },
        })}
      >
        {pageSizeOptions.map((option) => (
          <MenuItem key={option} value={option} sx={{ fontSize: 12 }}>
            {option}
          </MenuItem>
        ))}
      </Select>
      <Typography variant="caption" sx={{ color: "text.textGrey", whiteSpace: "nowrap" }}>
        {language === 'hi_IN' ? t("pagination.showing") : t("pagination.ofItems", { count: totalCount })}
      </Typography>
    </Box>
  );
}

ItemsControl.propTypes = {
  pageSize: PropTypes.number.isRequired,
  pageSizeOptions: PropTypes.arrayOf(PropTypes.number).isRequired,
  onPageSizeChange: PropTypes.func.isRequired,
  totalCount: PropTypes.number,
  t: PropTypes.func.isRequired,
};

export default function RewampPagination({
  count,
  page,
  onChange,
  siblingCount,
  boundaryCount,
  pageSize,
  basePageSize,
  onPageSizeChange,
  totalCount,
  summary,
}) {
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  // 7 page slots + 2 arrows (352px) overflow a 320px phone; without siblings it is at most 5 slots.
  const compact = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const { items } = usePagination({
    count,
    page,
    onChange: (_event, value) => onChange(value),
    siblingCount: compact ? 0 : siblingCount,
    boundaryCount,
  });

  const showPager = count >= 1; // marker shows even for a single page
  const hasSizeControl = typeof onPageSizeChange === "function" && pageSize != null;
  const pageSizeOptions = getPageSizeOptions(basePageSize);
  const hasRight = hasSizeControl || summary != null;

  if (!showPager && !hasRight) return null;

  return (
    <Box
      component="nav"
      aria-label={t("pagination.ariaLabel")}
      sx={{
        display: "flex",
        alignItems: "center",
        // Right cluster carries ml:auto so it stays right-aligned when it wraps onto its own line.
        justifyContent: hasRight ? "space-between" : "center",
        flexWrap: "wrap",
        gap: 2,
        width: "100%",
      }}
    >
      {showPager ? <PageControl items={items} t={t} /> : <Box />}

      {hasSizeControl ? (
        <ItemsControl
          pageSize={pageSize}
          pageSizeOptions={pageSizeOptions}
          onPageSizeChange={onPageSizeChange}
          totalCount={totalCount}
          t={t}
          language={language}
        />
      ) : summary != null ? (
        <Typography component="div" variant="caption" sx={{ color: "text.textGrey", ml: "auto" }}>
          {summary}
        </Typography>
      ) : null}
    </Box>
  );
}

RewampPagination.propTypes = {
  /** Total number of pages. */
  count: PropTypes.number.isRequired,
  /** Current page (1-based). */
  page: PropTypes.number.isRequired,
  /** Called with the next page number (1-based). */
  onChange: PropTypes.func.isRequired,
  /** Pages shown either side of the current page (MUI usePagination). */
  siblingCount: PropTypes.number,
  /** Pages always shown at the start/end (MUI usePagination). */
  boundaryCount: PropTypes.number,
  /** Current page size — with onPageSizeChange, enables the items-control dropdown. */
  pageSize: PropTypes.number,
  /** Base page size the dropdown choices derive from (options = base × [1..5]). */
  basePageSize: PropTypes.number,
  /** Called with the chosen page size; consumer resets to page 1. */
  onPageSizeChange: PropTypes.func,
  /** Total item count, shown as "of N items" beside the dropdown. */
  totalCount: PropTypes.number,
  /** Right-aligned summary node used only when no page-size dropdown is given. */
  summary: PropTypes.node,
};

RewampPagination.defaultProps = {
  siblingCount: 1,
  boundaryCount: 1,
  pageSize: null,
  basePageSize: DEFAULT_PAGE_SIZE,
  onPageSizeChange: null,
  totalCount: null,
  summary: null,
};
